import { Component, Input, OnInit, OnChanges, SimpleChanges, EventEmitter, Output, ChangeDetectorRef } from '@angular/core';

import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputFieldComponent } from 'src/app/components/form/input-field/input-field.component';
import { SelectFieldComponent } from 'src/app/components/form/select-field/select-field.component';
import { SelectOption } from 'src/app/components/form/select-field/select-field.config';
import { ConfirmationDialogService } from 'src/app/services/confirmation-dialog.service';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';
import { ProjectsService } from 'src/app/services/api/projects.service';
import { LoaderService } from 'src/app/services/loader.service';
import { forkJoin } from 'rxjs';
import { HttpParams } from '@angular/common/http';

@Component({
  selector: 'projects-batch',
  imports: [
    ReactiveFormsModule,
    InputFieldComponent,
    SelectFieldComponent
],
  templateUrl: './projects-batch.component.html',
  styleUrl: './projects-batch.component.scss'
})
export class ProjectsBatchComponent implements OnInit, OnChanges {
  @Input() projectId?: string;
  @Input() jurisdictionId?: string | number | null;
  @Output() onBatchUpdated = new EventEmitter<any>();

  batchForm: FormGroup;
  
  availableChecklists: any[] = [];
  
  selectedChecklistIds: Set<number> = new Set();
  
  selectedChecklistsForBatch: Set<number> = new Set();
  
  get filteredAvailableChecklists(): any[] {
    if (!Array.isArray(this.availableChecklists)) {
      return [];
    }
    const assignedChecklistIds = this.getAssignedChecklistIds();
    return this.availableChecklists.filter(checklist => !assignedChecklistIds.has(this.getChecklistId(checklist)));
  }
  getChecklistId(checklist: any): number {
    return checklist?.projectChecklistId;
  }

  private getChecklistName(checklist: any): string {
    const primaryName = checklist?.projectWorkinglist?.checklist?.name && 
      checklist?.projectWorkinglist?.name && 
      checklist?.projectWorkinglist?.platform?.name
      ? `${checklist.projectWorkinglist.checklist.name} - ${checklist.projectWorkinglist.name} - ${checklist.projectWorkinglist.platform.name}`
      : null;
    
    const secondaryName = checklist?.projectChecklist?.projectWorkinglist?.checklist?.name && 
      checklist?.projectChecklist?.projectWorkinglist?.name && 
      checklist?.projectChecklist?.projectWorkinglist?.platform?.name
      ? `${checklist.projectChecklist.projectWorkinglist.checklist.name} - ${checklist.projectChecklist.projectWorkinglist.name} - ${checklist.projectChecklist.projectWorkinglist.platform.name}`
      : null;
    
    return primaryName || secondaryName || checklist?.projectWorkinglist?.checklist?.name || '';
  }

  private getChecklistDescription(checklist: any): string {
    return checklist.projectWorkinglist?.checklist?.description || checklist.description || '';
  }

  private getChecklistEstimatedTime(checklist: any): number {
    return checklist.projectWorkinglist?.estimatedTime || checklist.estimated_time || 0;
  }
  
  batches: any[] = [];

  showCreateBatchModal = false;

  batchFilterControl: FormControl;
  batchFilterOptions: SelectOption[] = [];
  selectedBatchFilter: string | null = null;
  
  get filteredBatches(): any[] {
    if (!this.selectedBatchFilter || this.selectedBatchFilter === 'all') {
      return this.batches;
    }
    return this.batches.filter(batch => batch.projectBatchId.toString() === this.selectedBatchFilter);
  }
  
  expandedBatches: Set<number> = new Set();

  constructor(
    private fb: FormBuilder,
    private alertNotificationService: AlertNotificationService,
    private projectsService: ProjectsService,
    private loaderService: LoaderService,
    private cdr: ChangeDetectorRef,
    private confirmationDialogService: ConfirmationDialogService
  ) {
    this.batchForm = this.fb.group({
      batchName: ['', [Validators.required, Validators.minLength(3)]]
    });
    
    this.batchFilterControl = new FormControl('all');
  }

  ngOnInit(): void {
    if (this.projectId) {
      this.initializeData(this.jurisdictionId);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['jurisdictionId'] && !changes['jurisdictionId'].firstChange && this.projectId) {
      this.initializeData(this.jurisdictionId);
    }
  }

  private buildJurisdictionParams(jurisdictionId?: string | number | null): HttpParams {
    let params = new HttpParams();
    if (jurisdictionId !== null && jurisdictionId !== undefined && jurisdictionId !== '') {
      params = params.set('jurisdictionId', `${jurisdictionId}`);
    }
    return params;
  }

  private initializeData(jurisdictionId?: string | number | null): void {
    this.loaderService.startLoading('initialization');

    const params = this.buildJurisdictionParams(jurisdictionId);

    forkJoin([
      this.projectsService.listProjectBatch(Number(this.projectId)),
      this.projectsService.listProjectBatchChecklistAvailable(Number(this.projectId), { params })
    ]).subscribe({
      next: ([batchResponse, checklistResponse]: any[]) => {
        let batchesData = batchResponse.data?.projectBatches || [];
        if (!Array.isArray(batchesData)) {
          console.warn('Batches data is not an array:', batchesData);
          batchesData = [];
        }
        this.batches = batchesData;

        let checklistsData = checklistResponse.data?.projectChecklists || [];
        if (!Array.isArray(checklistsData)) {
          console.warn('Checklists data is not an array:', checklistsData);
          checklistsData = [];
        }
        this.availableChecklists = checklistsData;

        this.loadChecklistsForBatches();
        this.updateBatchFilterOptions();
        this.loaderService.stopLoading('initialization');
      },
      error: (error: any) => {
        console.error('Error initializing data:', error);
        this.batches = [];
        this.availableChecklists = [];
        this.alertNotificationService.error('Error loading batches and checklists');
        this.loaderService.stopLoading('initialization');
      }
    });
  }

  private updateBatchFilterOptions(): void {
    this.batchFilterOptions = [
      { key: 'all', value: 'All Batches' },
      ...this.batches.map(batch => ({
        key: batch.projectBatchId.toString(),
        value: batch.name
      }))
    ];
    
  }

  openCreateBatchModal(): void {
    this.showCreateBatchModal = true;
    document.body.style.overflow = 'hidden';
  }

  closeCreateBatchModal(): void {
    this.showCreateBatchModal = false;
    document.body.style.overflow = '';
  }

  createBatchFromModal(): void {
    this.createBatch();
    this.closeCreateBatchModal();
  }

  onBatchFilterChange(value: string): void {
    this.selectedBatchFilter = value;
    this.selectedChecklistsForBatch.clear();
  }

  private reloadAvailableChecklists(): void {
    if (!this.projectId) return;

    const params = this.buildJurisdictionParams(this.jurisdictionId);

    this.projectsService.listProjectBatchChecklistAvailable(Number(this.projectId), { params }).subscribe({
      next: (response: any) => {
        let checklistsData = response.data?.projectChecklists || [];

        if (!Array.isArray(checklistsData)) {
          console.warn('Checklists data is not an array:', checklistsData);
          checklistsData = [];
        }

        this.availableChecklists = checklistsData;
      },
      error: (error: any) => {
        console.error('Error reloading checklists:', error);
        this.availableChecklists = [];
      }
    });
  }

  private reloadBatchesAndChecklists(): void {
    if (!this.projectId) return;

    const params = this.buildJurisdictionParams(this.jurisdictionId);

    forkJoin([
      this.projectsService.listProjectBatch(Number(this.projectId)),
      this.projectsService.listProjectBatchChecklistAvailable(Number(this.projectId), { params })
    ]).subscribe({
      next: ([batchResponse, checklistResponse]: any[]) => {
        let batchesData = batchResponse.data?.projectBatches || [];
        if (!Array.isArray(batchesData)) {
          batchesData = [];
        }
        this.batches = batchesData;

        let checklistsData = checklistResponse.data?.projectChecklists || [];
        if (!Array.isArray(checklistsData)) {
          checklistsData = [];
        }
        this.availableChecklists = checklistsData;

        this.loadChecklistsForBatches();
        this.updateBatchFilterOptions();
      },
      error: (error: any) => {
        console.error('Error reloading batches and checklists:', error);
      }
    });
  }

  private loadChecklistsForBatches(): void {
    if (!this.projectId || this.batches.length === 0) return;

    const checklistLoadRequests = this.batches.map(batch =>
      this.projectsService.listProjectBatchChecklist(Number(this.projectId), batch.projectBatchId)
    );

    forkJoin(checklistLoadRequests).subscribe({
      next: (responses: any[]) => {
        responses.forEach((response, index) => {
          if (this.batches[index]) {
            this.batches[index].checklists = response.data?.projectBatchChecklists || response.data || [];
          }
        });
      },
      error: (error: any) => {
        console.error('Error loading checklists for batches:', error);
        this.batches.forEach(batch => {
          batch.checklists = [];
        });
      }
    });
  }

  toggleChecklistSelection(checklistId: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    if (this.selectedChecklistIds.has(checklistId)) {
      this.selectedChecklistIds.delete(checklistId);
    } else {
      this.selectedChecklistIds.add(checklistId);
    }
    
    this.cdr.markForCheck();
  }

  isChecklistSelected(checklistId: number): boolean {
    return this.selectedChecklistIds.has(checklistId);
  }

  createBatch(): void {
    if (this.batchForm.invalid) {
      this.alertNotificationService.error('Please enter a valid batch name');
      return;
    }

    if (this.selectedChecklistIds.size === 0) {
      this.alertNotificationService.error('Please select at least one checklist');
      return;
    }

    const batchName = this.batchForm.get('batchName')?.value;
    const selectedChecklistIdsArray = Array.from(this.selectedChecklistIds);

    const batchData = {
      name: batchName
    };

    this.loaderService.startLoading('create-batch');
    this.projectsService.createProjectBatch(Number(this.projectId), batchData).subscribe({
      next: (response: any) => {
        
        const projectBatchId = response.data?.projectBatchId || response.data?.projectBatch?.projectBatchId;
        
        if (!projectBatchId) {
          this.alertNotificationService.error('Error: Batch ID not returned from server');
          this.loaderService.stopLoading('create-batch');
          return;
        }

        const checklistAddRequests = selectedChecklistIdsArray.map(checklistId =>
          this.projectsService.createProjectBatchChecklist(
            Number(this.projectId),
            projectBatchId,
            { projectChecklistId: checklistId }
          )
        );

        forkJoin(checklistAddRequests).subscribe({
          next: (responses: any[]) => {
            this.batchForm.reset();
            this.selectedChecklistIds.clear();
            
            this.reloadBatchesAndChecklists();
            
            this.alertNotificationService.success(
              `Batch created successfully with ${responses.length} checklist${responses.length > 1 ? 's' : ''}`
            );
            
            this.onBatchUpdated.emit({ 
              type: 'batch_created', 
              batchId: projectBatchId,
              checklistsAdded: responses.length 
            });
            this.loaderService.stopLoading('create-batch');
          },
          error: (error: any) => {
            console.error('Error adding checklists to batch:', error);
            this.alertNotificationService.error('Batch created but error adding checklists');
            this.reloadBatchesAndChecklists();
            this.loaderService.stopLoading('create-batch');
          }
        });
      },
      error: (error: any) => {
        console.error('Error creating batch:', error);
        this.alertNotificationService.error(error.error?.message || 'Error creating batch');
        this.loaderService.stopLoading('create-batch');
      }
    });
  }

  requestDeleteBatch(batch: any): void {
    this.confirmationDialogService.open(
      `Are you sure you want to delete the batch "<strong>${batch.name}</strong>"?<br>This action cannot be undone.`,
      () => this.deleteBatch(batch.projectBatchId)
    );
  }

  private deleteBatch(batchId: number): void {
    if (!this.projectId) {
      this.alertNotificationService.error('Project ID is required');
      return;
    }

    this.loaderService.startLoading('delete-batch');
    this.projectsService.deleteProjectBatch(Number(this.projectId), batchId).subscribe({
      next: () => {
        const batchIndex = this.batches.findIndex(b => b.projectBatchId === batchId);
        if (batchIndex !== -1) {
          this.batches.splice(batchIndex, 1);
        }
        
        this.updateBatchFilterOptions();
        
        if (this.selectedBatchFilter === batchId.toString()) {
          this.batchFilterControl.setValue('all');
          this.selectedBatchFilter = 'all';
        }
        
        this.reloadAvailableChecklists();
        
        this.alertNotificationService.success('Batch deleted successfully');
        this.onBatchUpdated.emit({ type: 'batch_deleted', batchId });
        this.loaderService.stopLoading('delete-batch');
      },
      error: (error: any) => {
        console.error('Error deleting batch:', error);
        this.alertNotificationService.error(error.error?.message || 'Error deleting batch');
        this.loaderService.stopLoading('delete-batch');
      }
    });
  }

  requestRemoveChecklistFromBatch(batch: any, checklist: any): void {
    const checklistName = this.getChecklistName(checklist);
    const checklistId = checklist.projectChecklist.projectChecklistId;
    this.confirmationDialogService.open(
      `Are you sure you want to remove "<strong>${checklistName}</strong>" from batch "<strong>${batch.name}</strong>"?`,
      () => this.removeChecklistFromBatch(batch.projectBatchId, checklistId)
    );
  }

  private removeChecklistFromBatch(batchId: number, checklistId: number): void {
    if (!this.projectId) {
      this.alertNotificationService.error('Project ID is required');
      return;
    }

    const batch = this.batches.find(b => b.projectBatchId === batchId);
    if (!batch) {
      this.alertNotificationService.error('Batch not found');
      return;
    }
    
    const checklistInBatch = batch.checklists?.find((c: any) => c.projectChecklist.projectChecklistId === checklistId);
    if (!checklistInBatch) {
      this.alertNotificationService.error('Checklist not found in batch');
      return;
    }
    const projectBatchChecklistId = checklistInBatch.projectBatchChecklistId || checklistInBatch.projectChecklist.projectChecklistId;

    this.loaderService.startLoading('remove-checklist-batch');
    this.projectsService.deleteProjectBatchChecklist(
      Number(this.projectId),
      batchId,
      projectBatchChecklistId
    ).subscribe({
      next: () => {
        const checklistIndex = batch.checklists.findIndex((c: any) => c.projectChecklist.projectChecklistId === checklistId);
        if (checklistIndex !== -1) {
          batch.checklists.splice(checklistIndex, 1);
        }
        
        if (batch.checklists.length === 0) {
          this.alertNotificationService.warning(`Checklist removed. Batch "${batch.name}" is now empty.`);
        } else {
          this.alertNotificationService.success('Checklist removed from batch');
        }  
        this.reloadAvailableChecklists();
        
        this.onBatchUpdated.emit({ 
          type: 'checklist_removed', 
          batchId, 
          checklistId 
        });
        this.loaderService.stopLoading('remove-checklist-batch');
      },
      error: (error: any) => {
        console.error('Error removing checklist:', error);
        this.alertNotificationService.error(error.error?.message || 'Error removing checklist from batch');
        this.loaderService.stopLoading('remove-checklist-batch');
      }
    });
  }

  getBatchNameControl(): FormControl {
    return this.batchForm.get('batchName') as FormControl;
  }

  getTotalEstimatedTime(batch: any): number {
    if (!batch || !Array.isArray(batch.checklists)) {
      return 0;
    }
    return batch.checklists.reduce((total: number, checklist: any) => {
      return total + this.getChecklistEstimatedTime(checklist);
    }, 0);
  }

  getChecklistDisplayName(checklist: any): string {
    return this.getChecklistName(checklist);
  }

  getChecklistDisplayDescription(checklist: any): string {
    return this.getChecklistDescription(checklist);
  }

  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  toggleBatchExpansion(batchId: number): void {
    if (this.expandedBatches.has(batchId)) {
      this.expandedBatches.delete(batchId);
    } else {
      this.expandedBatches.add(batchId);
    }
  }
  isBatchExpanded(batchId: number): boolean {
    return this.expandedBatches.has(batchId);
  }

  private getAssignedChecklistIds(): Set<number> {
    const assignedIds = new Set<number>();
    if (!Array.isArray(this.batches)) {
      console.warn('Batches is not an array:', this.batches);
      return assignedIds;
    }
    
    this.batches.forEach(batch => {
      if (batch && batch.checklists && Array.isArray(batch.checklists)) {
        batch.checklists.forEach((batchChecklist: any) => {
          const checklistId = batchChecklist?.projectChecklist?.projectChecklistId;
          if (checklistId !== undefined && checklistId !== null) {
            assignedIds.add(checklistId);
          }
        });
      }
    });
    return assignedIds;
  }


  toggleChecklistForBatchSelection(checklistId: number, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    
    if (this.selectedChecklistsForBatch.has(checklistId)) {
      this.selectedChecklistsForBatch.delete(checklistId);
    } else {
      this.selectedChecklistsForBatch.add(checklistId);
    }
    
    this.cdr.markForCheck();
  }

  isChecklistForBatchSelected(checklistId: number): boolean {
    return this.selectedChecklistsForBatch.has(checklistId);
  }

  canShowAddToBatchButton(): boolean {
    return this.selectedBatchFilter !== null && 
           this.selectedBatchFilter !== 'all' && 
           this.selectedChecklistsForBatch.size > 0;
  }

  addChecklistsToBatch(): void {
    if (!this.selectedBatchFilter || this.selectedBatchFilter === 'all') {
      this.alertNotificationService.error('Please select a specific batch');
      return;
    }

    if (this.selectedChecklistsForBatch.size === 0) {
      this.alertNotificationService.error('Please select at least one checklist');
      return;
    }

    if (!this.projectId) {
      this.alertNotificationService.error('Project ID is required');
      return;
    }

    const batch = this.batches.find(b => b.projectBatchId.toString() === this.selectedBatchFilter);
    if (!batch) {
      this.alertNotificationService.error('Batch not found');
      return;
    }

    const newChecklistIds = Array.from(this.selectedChecklistsForBatch);

    this.loaderService.startLoading('add-checklists-batch');

    
    const checklistAddRequests = newChecklistIds.map(checklistId =>
      this.projectsService.createProjectBatchChecklist(
        Number(this.projectId),
        batch.projectBatchId,
        { projectChecklistId: checklistId }
      )
    );

    forkJoin(checklistAddRequests).subscribe({
      next: (responses: any[]) => {
        this.selectedChecklistsForBatch.clear();
        
        this.reloadBatchesAndChecklists();
        this.expandedBatches.add(batch.projectBatchId);
        
        this.alertNotificationService.success(
          `${responses.length} checklist${responses.length > 1 ? 's' : ''} added to batch "${batch.name}"`
        );
        
        this.onBatchUpdated.emit({ 
          type: 'checklists_added', 
          batchId: batch.projectBatchId, 
          checklistIds: newChecklistIds 
        });
        this.loaderService.stopLoading('add-checklists-batch');
      },
      error: (error: any) => {
        console.error('Error adding checklists to batch:', error);
        this.alertNotificationService.error('Error adding checklists to batch');
        this.loaderService.stopLoading('add-checklists-batch');
      }
    });
  }

}
